import angular from 'angular'
import {
  SERVER_ENVIRONMENT_LIST,
  LegacyServerEnvironmentItem
} from './legacy-constants'
import {
  serverDisplayLogLevels,
  stackTraceCaptureModes,
  buildServerSettingsFormCommon
} from './legacy-server-settings-shared'
import { PREFERENCE_PROPERTIES } from '../preferences'
import { logSuccess } from '../../../components/toaster'
import {
  SyslogSeverity,
  SyslogSettingsFormState,
  SyslogProtocol,
  defaultSyslogSettingsFormState
} from '../../../components/syslog'
import { SyslogAttackStatus } from '../../../components/syslog/syslog-attack-status'
import {
  LogLevel,
  StacktraceCaptureMode
} from '../../../components/servers-grid/shared'
import { ServerEnvironment } from '../../../components/server-environment'
import {
  LegacyServerFactory,
  GetOrgSettingsResp,
  ServerCleanupPolicy
} from './legacy-server-factory'
import { LogLevelDisplay } from '../../../components/server-settings/form-components/log-level-input'
export type ServerCleanupPolicyTimeUnit = 'MINUTES' | 'HOURS' | 'DAYS'

const serverCleanupTimeIntervals: Record<
  ServerCleanupPolicyTimeUnit,
  {
    readonly min: number
    readonly max: number
  }
> = {
  MINUTES: { min: 5, max: 60 },
  HOURS: { min: 1, max: 24 },
  DAYS: { min: 1, max: 365 }
}

const timeUnitsList: Array<{
  readonly value: ServerCleanupPolicyTimeUnit
  readonly name: string
}> = [
  { value: 'MINUTES', name: 'Minute(s)' },
  { value: 'HOURS', name: 'Hour(s)' },
  { value: 'DAYS', name: 'Day(s)' }
]

interface BuildUiStateFromApiResponse {
  readonly defaultEnvironment: ServerSettingsCtrlScope['defaultEnvironment']
  readonly telemetryEnabled: ServerSettingsCtrlScope['telemetryEnabled']
  readonly retainLibraryDataEnabled: ServerSettingsCtrlScope['retain_library_data']
  readonly serverCleanupEnhanced: ServerSettingsCtrlScope['serverCleanupEnhanced']
  readonly server: ServerSettingsCtrlScope['server']
}

// Build UI state for legacy controller from API response
export const buildUiStateFromApi = ({
  default_environment,
  telemetry_enabled,
  syslog_ip_address,
  syslog_port_number,
  syslog_facility_code,
  syslog_protocol,
  syslog_severity_exploited,
  syslog_severity_suspicious,
  syslog_severity_blocked,
  syslog_severity_blocked_perimeter,
  syslog_severity_probed,
  syslog_enabled,
  retain_library_data,
  server_cleanup_enhanced,
  ...settings
}: GetOrgSettingsResp): BuildUiStateFromApiResponse => {
  return {
    defaultEnvironment: default_environment,
    telemetryEnabled: telemetry_enabled,
    retainLibraryDataEnabled: retain_library_data,
    serverCleanupEnhanced: server_cleanup_enhanced,
    server: {
      ...buildServerSettingsFormCommon({
        default_environment,
        telemetry_enabled,
        syslog_enabled,
        retain_library_data,
        server_cleanup_enhanced,
        ...settings
      }),
      // Do not use values from server if syslog is disabled
      // If syslog has not previously been configured before,
      // these values will be null from the API.
      syslog: syslog_enabled
        ? {
            host: getDefaultSyslogValueIfNull('host', syslog_ip_address),
            port: getDefaultSyslogValueIfNull('port', syslog_port_number),
            facilityCode: getDefaultSyslogValueIfNull(
              'facilityCode',
              syslog_facility_code
            ),
            protocol: getDefaultSyslogValueIfNull(
              'protocol',
              syslog_protocol
            ) as SyslogProtocol,
            severityByAttackStatus: {
              [SyslogAttackStatus.EXPLOITED]:
                getDefaultSyslogSeverityByAttackStatusIfNull(
                  SyslogAttackStatus.EXPLOITED,
                  syslog_severity_exploited
                ),
              [SyslogAttackStatus.SUSPICIOUS]:
                getDefaultSyslogSeverityByAttackStatusIfNull(
                  SyslogAttackStatus.SUSPICIOUS,
                  syslog_severity_suspicious
                ),
              [SyslogAttackStatus.BLOCKED]:
                getDefaultSyslogSeverityByAttackStatusIfNull(
                  SyslogAttackStatus.BLOCKED,
                  syslog_severity_blocked
                ),
              [SyslogAttackStatus.BLOCKED_PERIMETER]:
                getDefaultSyslogSeverityByAttackStatusIfNull(
                  SyslogAttackStatus.BLOCKED_PERIMETER,
                  syslog_severity_blocked_perimeter
                ),
              [SyslogAttackStatus.PROBED]:
                getDefaultSyslogSeverityByAttackStatusIfNull(
                  SyslogAttackStatus.PROBED,
                  syslog_severity_probed
                )
            }
          }
        : defaultSyslogSettingsFormState
    }
  }
}

function getDefaultSyslogValueIfNull(key: string, val: null | any) {
  if (val === null) {
    return defaultSyslogSettingsFormState[key]
  }
  return val
}

function getDefaultSyslogSeverityByAttackStatusIfNull(
  status: SyslogAttackStatus,
  val?: null | SyslogSeverity
): SyslogSeverity {
  if (val === null || typeof val === 'undefined') {
    const defaultVal =
      defaultSyslogSettingsFormState.severityByAttackStatus[status]
    if (!defaultVal) {
      throw new Error('could not find default syslog severity by attack status')
    }
    return defaultVal
  }
  return val
}

export interface BuildServerSettingsRequestInput {
  isEOP: () => boolean
  telemetryEnabled?: boolean
  serverCleanupEnhanced?: boolean
  isDefaultEnvironment: boolean
  selectedEnvironment: LegacyServerEnvironmentItem
  defaultEnvironment: ServerEnvironment
  server: ScopeServer
  retainLibraryDataEnabled: boolean
}

// This is the shape sent to the API on save
// Endpoint used: PUT UserService.buildAPIUrl('/server/settings')
export interface SaveOrgSettingsBody {
  readonly assess: boolean
  readonly defend: boolean
  readonly log_level: LogLevel
  readonly environment: ServerEnvironment
  readonly sampling: boolean
  readonly stacktrace_capture_mode: StacktraceCaptureMode
  readonly sampling_baseline: number
  readonly sampling_frequency: number
  readonly sampling_window: number
  readonly bot_protection: boolean
  readonly server_cleanup_policy: ServerCleanupPolicy
  readonly auto_license_protection: boolean
  readonly server_default_environment?: ServerEnvironment
  readonly syslog_enabled: boolean
  readonly syslog_ip_address?: any
  readonly syslog_port_number?: any
  readonly syslog_facility_code?: any
  readonly syslog_protocol: SyslogProtocol
  readonly syslog_connection_type?: any
  readonly syslog_severity_exploited?: SyslogSeverity
  readonly syslog_severity_suspicious?: SyslogSeverity
  readonly syslog_severity_blocked?: SyslogSeverity
  readonly syslog_severity_blocked_perimeter?: SyslogSeverity
  readonly syslog_severity_probed?: SyslogSeverity
  readonly syslog_severity_probed_perimeter?: SyslogSeverity
  readonly telemetry_enabled?: boolean
  readonly retain_library_data?: boolean
  server_cleanup_enhanced?: boolean
}

// Maps UI state to API shape
export function buildServerSettingsRequest({
  isEOP,
  telemetryEnabled,
  serverCleanupEnhanced,
  isDefaultEnvironment,
  selectedEnvironment,
  defaultEnvironment,
  retainLibraryDataEnabled,
  server: {
    assess,
    defend,
    logLevel,
    environment,
    sampling,
    stacktrace,
    baseline,
    frequency,
    window,
    botProtection,
    server_cleanup_policy,
    autoLicenseProtection,
    syslog_enabled,
    syslog: { host, port, protocol, facilityCode, severityByAttackStatus }
  }
}: BuildServerSettingsRequestInput): SaveOrgSettingsBody {
  const settings = {
    server_default_environment: isDefaultEnvironment
      ? selectedEnvironment.value
      : defaultEnvironment,

    assess,
    defend,
    log_level: logLevel.toUpperCase() as LogLevel,
    environment: environment.value,
    sampling,
    stacktrace_capture_mode: stacktrace,
    sampling_baseline: baseline,
    sampling_frequency: frequency,
    sampling_window: window,
    bot_protection: botProtection,
    server_cleanup_policy,
    auto_license_protection: autoLicenseProtection,

    // Syslog
    syslog_enabled,
    syslog_ip_address: host,
    syslog_port_number: port,
    syslog_facility_code: facilityCode,
    syslog_protocol: protocol,

    // Syslog severity levels by attack status
    syslog_severity_exploited:
      severityByAttackStatus[SyslogAttackStatus.EXPLOITED],
    syslog_severity_suspicious:
      severityByAttackStatus[SyslogAttackStatus.SUSPICIOUS],
    syslog_severity_blocked_perimeter:
      severityByAttackStatus[SyslogAttackStatus.BLOCKED_PERIMETER],
    syslog_severity_blocked: severityByAttackStatus[SyslogAttackStatus.BLOCKED],
    syslog_severity_probed: severityByAttackStatus[SyslogAttackStatus.PROBED],
    retain_library_data: retainLibraryDataEnabled,

    server_cleanup_enhanced: serverCleanupEnhanced
  }
  return !isEOP()
    ? {
        ...settings,
        telemetry_enabled: telemetryEnabled
      }
    : settings
}

export interface ScopeServerBase {
  readonly assess: any
  readonly defend: any
  readonly logLevel: LogLevelDisplay
  readonly environment: LegacyServerEnvironmentItem
  sampling: any
  readonly stacktrace: any
  readonly baseline: any
  readonly frequency?: any
  readonly window?: any
  readonly botProtection: boolean
  readonly server_cleanup_policy: ServerCleanupPolicy
  autoLicenseProtection: boolean
  readonly syslog_enabled: boolean
}

interface ScopeServer extends ScopeServerBase {
  syslog: SyslogSettingsFormState
}

interface ServerSettingsCtrlScope {
  $watch: angular.IScope['$watch']
  isDefaultEnvironment: boolean
  isDefaultCheckboxDisabled: boolean
  selectedEnvironment: LegacyServerEnvironmentItem
  defaultEnvironment: ServerEnvironment
  telemetryEnabled: any
  retain_library_data: boolean
  retainLibraryDataEnabled: any
  serverCleanupEnhanced: boolean
  server: ScopeServer
  model: {
    syslogFormIsValid: boolean
    environment: LegacyServerEnvironmentItem[]
    license_auto_override: any
  }
  protection: {
    total: number
    used: number
    percent: number
  }
  isEOP: () => boolean
  form: angular.IFormController
}

export const handleProtectionBreakdownResponse = ({ total, used }) => {
  return {
    total,
    used,
    percent: Math.round((used / total) * 100)
  }
}

export const getDefaultScope = () => ({
  model: {
    syslogFormIsValid: true,
    license_auto_override: false,
    environment: SERVER_ENVIRONMENT_LIST,
    units: timeUnitsList,
    logLevel: serverDisplayLogLevels,
    stackTraceLevel: stackTraceCaptureModes
  },
  protection: {
    total: 0,
    used: 0,
    percent: 0
  },
  settings: {},
  select: {},
  server: {},
  org: {},
  selectedEnvironment: {},
  defaultEnvironment: {},
  isDefaultEnvironment: false
})

// This is a legacy AngularJS controller that has been moved to the typescript layer.
export function ServerSettingsCtrl(
  $rootScope: any,
  $scope: ServerSettingsCtrlScope,
  $q: angular.IQService,
  $translate: LegacyTranslateService,
  $http: angular.IHttpService,
  UserService: LegacyUserService,
  permissions: LegacyPermissionsService,
  ServerFactory: LegacyServerFactory,
  ErrorService: LegacyErrorService,
  preferencesService: LegacyPreferencesService,
  LicenseService: LegacyLicenseService,
  dialogs: LegacyDialogsService
) {
  'ngInject'

  function init() {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    _getOrgServerSettings().then(() => {
      const selectedEnvironment = $scope.model.environment.find(
        ({ value }) => value === $scope.defaultEnvironment
      )
      if (!selectedEnvironment) {
        throw new Error('selected environment not defined')
      }
      $scope.selectedEnvironment = selectedEnvironment
      $scope.isDefaultEnvironment = true
      $scope.isDefaultCheckboxDisabled = true
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      _getLicenseAutoOverride()
      if (isRASPEnabled()) {
        _loadProtectionLicenseBreakdown()
      }
    })

    // prevent cleaning ui-select selected value
    // a better solution is to migrate to use the react component instead.
    $scope.$watch(
      'server.server_cleanup_policy.unit',
      (newValue, oldValue: ServerCleanupPolicyTimeUnit) => {
        if (!newValue) {
          $scope.server.server_cleanup_policy.unit = oldValue
        }
      }
    )
  }

  function _getLicenseAutoOverride() {
    return preferencesService
      .getBooleanValue(PREFERENCE_PROPERTIES.LICENSE_AUTO_OVERRIDE)
      .then((preferenceValue) => {
        $scope.model.license_auto_override = preferenceValue
      })
  }

  function _loadProtectionLicenseBreakdown() {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    LicenseService.getProtectionBreakdown().then(
      (breakdown) =>
        ($scope.protection = handleProtectionBreakdownResponse(breakdown))
    )
  }

  angular.extend($scope, getDefaultScope())

  angular.extend($scope, {
    isRASPEnabled,
    isEOP: permissions.isEOP,
    saveOrgServerSettings,
    changeOrgAssess,
    changeServerSettingsEnvironment,
    getMinTimeValue,
    getMaxTimeValue,
    updateDefend,
    toggleTelemetry,
    toggleRetainLibraryData,
    isAllowedToToggleServerCleanup,
    toggleServerCleanup,
    getSyslogFormData,
    onSyslogFormChange,
    onSyslogFormValidityChange,
    isSaveButtonDisabled,
    getProtectSettingsFormData,
    onProtectSettingsChange,
    getProtectLicenseBreakdown
  })

  function getProtectSettingsFormData() {
    return {
      defend: $scope.server.defend,
      autoLicenseProtection: $scope.server.autoLicenseProtection,
      botProtection: $scope.server.botProtection,
      syslogEnabled: $scope.server.syslog_enabled
    }
  }

  function onProtectSettingsChange(protectSettings) {
    Object.keys(protectSettings).forEach((key) => {
      $scope.server[key] = protectSettings[key]
    })
    $scope.form.$setDirty()
  }

  function getProtectLicenseBreakdown() {
    return {
      total: $scope.protection.total,
      used: $scope.protection.used
    }
  }

  function isSaveButtonDisabled() {
    return (
      !$scope.model.syslogFormIsValid ||
      $scope.form.$invalid ||
      $scope.form.$pristine
    )
  }

  function getSyslogFormData() {
    return $scope.server.syslog
  }

  function onSyslogFormValidityChange(formIsValid: boolean) {
    $scope.model.syslogFormIsValid = formIsValid
  }

  function onSyslogFormChange(newData: SyslogSettingsFormState) {
    $scope.server.syslog = newData
    $scope.form.$setDirty()
  }

  function isRASPEnabled() {
    return permissions.isRASPEnabled()
  }

  function updateDefend() {
    $scope.server.autoLicenseProtection = !$scope.server.defend ? false : true
  }

  function getMinTimeValue(unit?: ServerCleanupPolicyTimeUnit) {
    if (unit) {
      return serverCleanupTimeIntervals[unit].min
    }
  }

  function getMaxTimeValue(unit?: ServerCleanupPolicyTimeUnit) {
    if (unit) {
      return serverCleanupTimeIntervals[unit].max
    }
  }

  function toggleTelemetry(enabled: boolean) {
    $scope.form.$setDirty()
    $scope.telemetryEnabled = enabled
  }

  function toggleRetainLibraryData(enabled: boolean) {
    $scope.form.$setDirty()
    $scope.retainLibraryDataEnabled = enabled
  }

  function toggleServerCleanup(enabled: boolean) {
    $scope.form.$setDirty()
    $scope.serverCleanupEnhanced = enabled
  }

  function isAllowedToToggleServerCleanup() {
    if ($rootScope.splitClient) {
      return (
        $rootScope.splitClient.getTreatment(
          'PROD-3060_enhanced_server_cleanup'
        ) === 'on'
      )
    }

    return false
  }

  // Builds API body and sends PUT request to update server settings
  function saveOrgServerSettings() {
    const deferred = $q.defer()

    $http
      .put<{
        readonly messages: string[]
      }>(UserService.buildAPIUrl('/server/settings'), {
        ...buildServerSettingsRequest($scope),
        // TODO Remove probed perimeter prop after TS-2505 is complete.
        // This is necessary because we have removed this property from the UI form
        // but it is still required in the backend validator when defend is true.
        syslog_severity_probed_perimeter: 'WARNING'
      })
      .then((response) => {
        const data = response.data
        if (!data) {
          throw new Error(
            'save org server settings failed: response.data is not defined'
          )
        }
        logSuccess(data.messages[0])
        $scope.form.$dirty = false
        deferred.resolve()
        $scope.isDefaultCheckboxDisabled = true
      })
      .catch((response) => {
        ErrorService.processError(response)
        deferred.reject()
      })

    return deferred.promise
  }

  function changeOrgAssess() {
    $scope.server.sampling = false
  }

  function changeServerSettingsEnvironment(env: LegacyServerEnvironmentItem) {
    // Do nothing if selected environment is already loaded
    if (env === $scope.selectedEnvironment) {
      return
    }

    // Check for unsaved changes and load new org defaults
    if ($scope.form.$dirty) {
      _confirmUnsavedChanges(env)
    } else {
      _loadEnvironmentDefaultSettings(env)
    }
  }

  function _loadEnvironmentDefaultSettings(env: LegacyServerEnvironmentItem) {
    // Load new server settings for selected environment
    $scope.selectedEnvironment = env
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    _getOrgServerSettings($scope.selectedEnvironment.value).then(() => {
      $scope.isDefaultEnvironment =
        $scope.selectedEnvironment.value === $scope.defaultEnvironment
      $scope.isDefaultCheckboxDisabled = env.value === $scope.defaultEnvironment
    })
  }

  function _confirmUnsavedChanges(env: LegacyServerEnvironmentItem) {
    const transl = {
      action_save: $translate.instant('action_save'),
      action_doNotSave: $translate.instant('action_doNotSave'),
      title_settings_unsavedChanges: $translate.instant(
        'title_settings_unsavedChanges'
      ),
      detail_settings_unsavedChangesConfirmation: $translate.instant(
        'detail_settings_unsavedChangesConfirmation'
      )
    }

    const dlg = dialogs.confirm(
      transl.title_settings_unsavedChanges,
      transl.detail_settings_unsavedChangesConfirmation,
      { size: 'sm', yes: transl.action_save, no: transl.action_doNotSave }
    )

    dlg.result.then(
      () => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        saveOrgServerSettings()
        _loadEnvironmentDefaultSettings(env)
      },
      () => {
        _loadEnvironmentDefaultSettings(env)
        $scope.form.$dirty = false
      }
    )
  }

  function _getOrgServerSettings(environment?: ServerEnvironment) {
    return _selectOrgServerSettings(environment)
      .then((response) => {
        if (response.data) {
          const {
            server,
            defaultEnvironment,
            telemetryEnabled,
            retainLibraryDataEnabled,
            serverCleanupEnhanced
          } = buildUiStateFromApi(response.data.settings)
          $scope.server = server
          $scope.defaultEnvironment = defaultEnvironment
          $scope.telemetryEnabled = telemetryEnabled
          $scope.retainLibraryDataEnabled = retainLibraryDataEnabled
          $scope.serverCleanupEnhanced = serverCleanupEnhanced

          $scope.form.$setPristine()
        }
      })
      .catch(ErrorService.processError)
  }

  function _selectOrgServerSettings(
    environment?: ServerEnvironment
  ): angular.IHttpPromise<{
    readonly settings: GetOrgSettingsResp
  }> {
    if (environment) {
      return ServerFactory.getOrgSettingsForEnvironment(environment)
    }
    // Returns org's default environment server settings
    return $http.get(UserService.buildAPIUrl('/server/settings'))
  }

  init()
}
